import { useEffect, useRef, useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ChevronDown, ChevronRight, Info, Link2, ListPlus, Sparkles } from 'lucide-react';
import * as api from '../api';
import { CF_ATTRIBUTE_TYPES, humanizeColumn } from '../customFields';
import type {
  CustomFieldAction,
  CustomFieldCandidate,
  CustomFieldColumn,
  CustomFieldDecision,
  CustomFieldDecisions,
  CustomFieldsModel,
} from '../types';

interface CustomFieldsMappingProps {
  code: string;
  model: CustomFieldsModel;
  decisions: CustomFieldDecisions;
  onChange: (updater: (prev: CustomFieldDecisions) => CustomFieldDecisions) => void;
}

/** Valeur sentinelle du select « Champ V2 » pour la création d'un nouveau champ. */
const NEW_FIELD = '__new__';

/**
 * Mapping des colonnes CSV non natives vers des champs personnalisés V2 :
 * pour chaque colonne supplémentaire d'un fichier utilisateurs, choisir de
 * l'importer — vers un champ V2 EXISTANT du rôle (natif compris, aucun
 * doublon créé) ou en créant un nouveau champ (type + libellé) — ou de
 * l'ignorer. Pré-remplissable par IA (champ existant proposé en priorité).
 * Les valeurs sont rattachées à chaque utilisateur après l'import réel.
 */
export function CustomFieldsMapping({ code, model, decisions, onChange }: CustomFieldsMappingProps) {
  const [aiBusy, setAiBusy] = useState(false);
  const [aiMessage, setAiMessage] = useState<string | null>(null);
  // Pas de flag de cleanup : en StrictMode le cleanup annulerait la 1ʳᵉ
  // invocation et le garde ref sauterait la 2ᵉ (même piège que l'étape EN).
  const aiRanRef = useRef<CustomFieldsModel | null>(null);

  const setDecision = (logicalName: string, column: string, patch: Partial<CustomFieldDecision>) => {
    onChange((prev) => {
      const next: CustomFieldDecisions = { ...prev };
      next[logicalName] = { ...(next[logicalName] ?? {}) };
      const current = next[logicalName][column] ?? {
        action: 'import' as CustomFieldAction,
        typeCode: 'STRING',
        label: humanizeColumn(column),
      };
      next[logicalName][column] = { ...current, ...patch, source: patch.source ?? 'manual' };
      return next;
    });
  };

  // Pré-mapping IA automatique à l'arrivée sur l'étape (relançable via le
  // bouton) — les corrections manuelles ne sont jamais écrasées.
  useEffect(() => {
    if (aiRanRef.current === model) return;
    aiRanRef.current = model;
    void runAiPremap();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  const runAiPremap = async () => {
    setAiBusy(true);
    setAiMessage(null);
    try {
      const columns = Object.entries(model.files).flatMap(([logicalName, file]) =>
        file.columns.map((col) => ({
          id: `${logicalName}:${col.column}`,
          label: col.column,
          samples: col.samples,
          candidates: file.attributes ?? [],
        })),
      );
      const res = await api.premapCustomFieldsAi(code, columns);
      if (!res.enabled) {
        setAiMessage('Le pré-mapping IA n’est pas activé sur ce serveur.');
        return;
      }
      let applied = 0;
      onChange((prev) => {
        const next: CustomFieldDecisions = { ...prev };
        for (const s of res.suggestions) {
          const sep = s.id.indexOf(':');
          if (sep < 0) continue;
          const logicalName = s.id.slice(0, sep);
          const column = s.id.slice(sep + 1);
          if (!next[logicalName]?.[column]) continue;
          // On ne touche pas aux lignes déjà corrigées à la main.
          if (next[logicalName][column].source === 'manual') continue;
          next[logicalName] = { ...next[logicalName] };
          next[logicalName][column] = {
            ...next[logicalName][column],
            ...(s.typeCode ? { typeCode: s.typeCode } : {}),
            ...(s.label ? { label: s.label } : {}),
            // Champ existant proposé par l'IA : prioritaire (pas de doublon).
            ...(s.existingAttributeId !== undefined
              ? { existingAttributeId: s.existingAttributeId }
              : {}),
            source: 'ai',
            confidence: s.confidence,
          };
          applied++;
        }
        return next;
      });
      setAiMessage(
        applied > 0
          ? `${applied} colonne(s) pré-remplie(s) par l’IA — vérifiez avant d’importer.`
          : 'Aucune suggestion applicable.',
      );
    } catch {
      setAiMessage('Le pré-mapping IA a échoué. Vous pouvez mapper manuellement.');
    } finally {
      setAiBusy(false);
    }
  };

  return (
    <div className="space-y-6">
      <Alert>
        <Info className="h-4 w-4" />
        <AlertDescription className="flex flex-wrap items-center justify-between gap-3">
          <span>
            Des colonnes <strong>en plus des colonnes standard</strong> ont été détectées dans vos
            fichiers. Elles peuvent alimenter un <strong>champ existant</strong> de l’école ou
            devenir de nouveaux <strong>champs personnalisés</strong> : les valeurs sont rattachées
            à chaque utilisateur après l’import.
          </span>
          <Button size="sm" variant="outline" onClick={runAiPremap} disabled={aiBusy}>
            <Sparkles className={`mr-2 h-4 w-4 ${aiBusy ? 'animate-pulse' : ''}`} />
            {aiBusy ? 'Analyse IA…' : 'Pré-remplir avec l’IA'}
          </Button>
        </AlertDescription>
      </Alert>
      {aiMessage && <p className="text-sm text-muted-foreground">{aiMessage}</p>}

      {Object.entries(model.files).map(([logicalName, file]) => (
        <FileCard
          key={logicalName}
          logicalName={logicalName}
          label={file.label}
          columns={file.columns}
          attributes={file.attributes ?? []}
          decisions={decisions[logicalName] ?? {}}
          onSet={(column, patch) => setDecision(logicalName, column, patch)}
        />
      ))}
    </div>
  );
}

function FileCard({
  logicalName,
  label,
  columns,
  attributes,
  decisions,
  onSet,
}: {
  logicalName: string;
  label: string;
  columns: CustomFieldColumn[];
  attributes: CustomFieldCandidate[];
  decisions: Record<string, CustomFieldDecision>;
  onSet: (column: string, patch: Partial<CustomFieldDecision>) => void;
}) {
  const [open, setOpen] = useState(true);
  const toImport = columns.filter((c) => decisions[c.column]?.action !== 'ignore').length;
  const attributeName = (id: string | null | undefined) =>
    attributes.find((a) => a.id === id)?.name ?? null;

  return (
    <Card>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center justify-between gap-3 px-5 py-4 text-left"
      >
        <span className="flex items-center gap-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-violet-100 text-violet-600">
            <ListPlus className="h-5 w-5" />
          </span>
          <span className="text-base font-semibold">
            Champs personnalisés — {label}{' '}
            <span className="font-normal text-muted-foreground">(fichier {logicalName})</span>
          </span>
        </span>
        <span className="flex items-center gap-2">
          <Badge variant={toImport > 0 ? 'success' : 'secondary'}>
            {toImport}/{columns.length} à importer
          </Badge>
          {open ? (
            <ChevronDown className="h-5 w-5 text-muted-foreground" />
          ) : (
            <ChevronRight className="h-5 w-5 text-muted-foreground" />
          )}
        </span>
      </button>

      {open && (
        <CardContent>
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-left">
                <tr>
                  <th className="px-3 py-2">Colonne du fichier</th>
                  <th className="px-3 py-2">Action</th>
                  <th className="px-3 py-2">Champ V2</th>
                  <th className="px-3 py-2">Type du champ</th>
                  <th className="px-3 py-2">Libellé du champ</th>
                </tr>
              </thead>
              <tbody>
                {columns.map((col) => {
                  const d = decisions[col.column] ?? {
                    action: 'import' as CustomFieldAction,
                    typeCode: col.suggestedType,
                    label: humanizeColumn(col.column),
                    existingAttributeId: col.existingMatch?.id ?? null,
                  };
                  const mapsToExisting = Boolean(d.existingAttributeId);
                  return (
                    <tr key={col.column} className="border-t align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium font-mono">{col.column}</div>
                        <div className="mt-0.5 max-w-xs truncate text-xs text-muted-foreground">
                          {col.filled}/{col.rows} renseignées
                          {col.samples.length > 0 && <> — ex : {col.samples.join(' · ')}</>}
                        </div>
                      </td>
                      <td className="px-3 py-2">
                        <Select
                          value={d.action}
                          onValueChange={(action) =>
                            onSet(col.column, { action: action as CustomFieldAction })
                          }
                        >
                          <SelectTrigger className="w-32">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="import">Importer</SelectItem>
                            <SelectItem value="ignore">Ignorer</SelectItem>
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="px-3 py-2">
                        {d.action === 'import' && (
                          <div className="space-y-1">
                            <Select
                              value={d.existingAttributeId ?? NEW_FIELD}
                              onValueChange={(v) =>
                                onSet(col.column, {
                                  existingAttributeId: v === NEW_FIELD ? null : v,
                                })
                              }
                            >
                              <SelectTrigger className="w-56">
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value={NEW_FIELD}>➕ Créer un nouveau champ</SelectItem>
                                {attributes.map((a) => (
                                  <SelectItem key={a.id} value={a.id}>
                                    {a.name}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {mapsToExisting ? (
                              <Badge variant="secondary" className="text-xs">
                                <Link2 className="mr-1 h-3 w-3" />
                                Réutilisera « {attributeName(d.existingAttributeId) ?? '?'} »
                              </Badge>
                            ) : (
                              <Badge variant="outline" className="text-xs">
                                Sera créé
                              </Badge>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {d.action === 'import' && (
                          <div className="flex items-center gap-1.5">
                            <Select
                              value={d.typeCode}
                              onValueChange={(typeCode) => onSet(col.column, { typeCode })}
                            >
                              <SelectTrigger className="w-44">
                                <SelectValue placeholder="Type…" />
                              </SelectTrigger>
                              <SelectContent>
                                {CF_ATTRIBUTE_TYPES.map((t) => (
                                  <SelectItem key={t.code} value={t.code}>
                                    {t.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                            {d.source === 'ai' && (
                              <Badge variant="secondary" title="Suggestion IA">
                                <Sparkles className="mr-1 h-3 w-3" />
                                IA
                              </Badge>
                            )}
                          </div>
                        )}
                      </td>
                      <td className="px-3 py-2">
                        {d.action === 'import' && !mapsToExisting && (
                          <Input
                            className="w-56"
                            value={d.label}
                            onChange={(e) => onSet(col.column, { label: e.target.value })}
                            placeholder={humanizeColumn(col.column)}
                          />
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </CardContent>
      )}
    </Card>
  );
}
